import { MessageCircle, Phone, Plus, X } from 'lucide-react';
import { GroupPicker } from '../../whatsapp/GroupPicker';
import { WaContactSearchInput } from '../../whatsapp/WaContactSearchInput';

// Input slim in stile pannello: bordo solo sotto, si accende sul focus
const slimInputCls =
  'w-full bg-transparent py-1 text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.45)] dark:placeholder-gray-500 border-0 border-b border-[rgba(55,53,47,0.16)] dark:border-white/15 focus:border-[#2383E2] outline-none transition-colors rounded-none';

const sectionLabelCls =
  'block text-[11px] font-medium uppercase tracking-wide text-[#787774] dark:text-gray-500 mb-1';

interface WhatsappGroupFieldsProps {
  clientId: string;
  groupNumbers: string[];
  onNumbersChange: (numbers: string[]) => void;
  groupName: string;
  onGroupName: (v: string) => void;
  /** '' = crea un nuovo gruppo; JID = riusa un gruppo esistente del cliente. */
  reuseGroupJid: string;
  onReuseGroupJid: (v: string) => void;
  clientName?: string;
  /** true = evidenzia i campi mancanti (dopo tentativo di conferma). */
  showMissing: boolean;
}

/**
 * Campi gruppo WhatsApp del gate dati cliente (token `whatsapp_group`) in
 * stile pannello: label in maiuscoletto, picker chiuso di default, input slim.
 */
export function WhatsappGroupFields({
  clientId, groupNumbers, onNumbersChange, groupName, onGroupName,
  reuseGroupJid, onReuseGroupJid, clientName, showMissing,
}: WhatsappGroupFieldsProps) {
  const setGroupNumber = (idx: number, v: string) =>
    onNumbersChange(groupNumbers.map((n, i) => (i === idx ? v : n)));
  const addGroupNumber = () => onNumbersChange([...groupNumbers, '']);
  const removeGroupNumber = (idx: number) =>
    onNumbersChange(groupNumbers.length > 1 ? groupNumbers.filter((_, i) => i !== idx) : groupNumbers);

  return (
    <div className="space-y-3 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-white/10 p-3">
      <div className="flex items-start gap-2">
        <MessageCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#1FA855]" strokeWidth={1.5} />
        <p className="text-[12px] text-[#787774] dark:text-gray-400">
          Alla firma del contratto verrà gestito un{' '}
          <span className="font-medium text-[#37352F] dark:text-white">gruppo WhatsApp con il cliente</span>:
          creane uno nuovo o riusane uno già esistente.
        </p>
      </div>

      {/* Scelta gruppo: nuovo o esistente (picker chiuso di default) */}
      <div>
        <label className={sectionLabelCls}>Gruppo WhatsApp</label>
        <GroupPicker clientId={clientId} value={reuseGroupJid} onChange={onReuseGroupJid} />
        {reuseGroupJid && (
          <p className="mt-1 text-[11px] text-[#787774] dark:text-gray-500">
            I numeri qui sotto verranno aggiunti a questo gruppo esistente (chi è già dentro non viene toccato).
          </p>
        )}
      </div>

      {/* Numeri */}
      <div>
        <label className={sectionLabelCls}>
          Numeri da aggiungere{' '}
          {reuseGroupJid ? (
            <span className="normal-case tracking-normal">(opzionale)</span>
          ) : (
            <span className="text-[#EB5757]">*</span>
          )}
        </label>
        <div className="space-y-1">
          {groupNumbers.map((num, idx) => (
            <div key={idx} className="group/num flex items-center gap-2">
              <Phone className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
              <WaContactSearchInput
                value={num}
                onChange={(v) => setGroupNumber(idx, v)}
                onPickContact={(number) => setGroupNumber(idx, number)}
                placeholder="+39 333 1234567 o cerca per nome"
                className="min-w-0 flex-1"
                inputClassName={`${slimInputCls} ${showMissing && !reuseGroupJid && !num.trim() ? '!border-[#EB5757]' : ''}`}
              />
              {groupNumbers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeGroupNumber(idx)}
                  className="flex-shrink-0 text-[rgba(55,53,47,0.3)] opacity-0 transition-opacity hover:text-red-500 group-hover/num:opacity-100 dark:text-gray-600"
                  aria-label="Rimuovi numero"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addGroupNumber}
          className="mt-1.5 flex items-center gap-1 rounded px-1 py-0.5 text-[11.5px] text-[#337EA9] hover:bg-[rgba(55,53,47,0.06)] dark:text-[#529CCA] dark:hover:bg-white/[0.06]"
        >
          <Plus className="h-3 w-3" strokeWidth={1.5} />
          Aggiungi numero
        </button>
        {showMissing && !reuseGroupJid && groupNumbers.filter((n) => n.trim()).length === 0 && (
          <p className="mt-1 text-[11px] text-[#EB5757]">Inserisci almeno un numero WhatsApp.</p>
        )}
      </div>

      {/* Nome gruppo — solo per un nuovo gruppo (non si rinomina un gruppo esistente) */}
      {!reuseGroupJid && (
        <div>
          <label className={sectionLabelCls}>
            Nome del gruppo <span className="text-[#EB5757]">*</span>
          </label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => onGroupName(e.target.value)}
            placeholder={clientName ? `${clientName} <> Obly` : 'Nome del gruppo WhatsApp'}
            className={`${slimInputCls} ${showMissing && !groupName.trim() ? '!border-[#EB5757]' : ''}`}
          />
          <p className="mt-1 text-[11px] text-[#787774] dark:text-gray-500">
            È il titolo che vedrà il cliente nel gruppo.
          </p>
          {showMissing && !groupName.trim() && (
            <p className="mt-1 text-[11px] text-[#EB5757]">Inserisci il nome del gruppo.</p>
          )}
        </div>
      )}
    </div>
  );
}
